import { useMutation, useQueryClient } from '@tanstack/react-query';

import { localStorage } from '@change-corgi/core/storage';
import { useNavigate } from '@change-corgi/core-react/router';
import { useTracking } from '@change-corgi/core-react/tracking';

import {
	DraftClient,
	findOrCreateDraftAuthorId,
	InMemoryAdapter,
	LocalStorageAdapter,
} from '#src/app/shared/api/petitionDraft';

type Options = Readonly<{
	// Which homepage hero the user came from, echoed into the SAP redirect for attribution.
	sourceLocation: 'homepage_primary_button' | 'homepage_tiktok';
}>;

/**
 * Freeform-text path shared by the homepage hero variants: seed an `ai_draft` with the raw
 * text as `issuePrompt` and hand off to SAP's `/intro` with `new_draft=true`, which routes a
 * fresh draft on to the personal-story ("why") step.
 */
export function useCreateIssueDraft({ sourceLocation }: Options) {
	const navigate = useNavigate();
	const track = useTracking();
	const queryClient = useQueryClient();
	const draftAuthorId = findOrCreateDraftAuthorId({ storage: localStorage, queryClient });

	const draftClient = new DraftClient([new LocalStorageAdapter(localStorage), new InMemoryAdapter(queryClient)]);

	return useMutation({
		mutationFn: async (draftInput: string) => {
			const draft = { input: { issuePrompt: draftInput }, type: 'ai_draft' as const };
			return draftClient.createDraft(draftAuthorId, draft);
		},
		onSuccess: () => {
			navigate(`/start-a-petition/intro?source_location=${sourceLocation}&new_draft=true`);
		},
		onError: () => {
			void track('homepage_sap_draft_submission_error');
		},
	});
}
